import { SaveOutlined } from "@ant-design/icons";
import { Button, FormInstance } from "antd";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ContentType,
  EncodingType,
  IntegrationFormData,
  IntegrationProtocol,
} from "../../../api";
import { formatJsonForApi, formatXmlForApi } from "../../../util/formatting";
import { useIntegrationStateContext } from "../context/IntegrationStateContext";

interface SaveIntegrationButtonProps {
  form: FormInstance<IntegrationFormData>;
}

export function SaveIntegrationButton({ form }: SaveIntegrationButtonProps) {
  const { t } = useTranslation();

  const {
    updateIntegrationState,
    activeCell,
    setActiveCell,
    setUpdateIntegrationState,
    updateIntegration,
    createIntegrationTemplate,
    refreshIntegrations,
    refreshIntegration,
    refreshIntegrationTemplate,
  } = useIntegrationStateContext();

  const onUpdateHttpIntegration = async () => {
    const fieldValues = await form.getFieldsValue();

    const bodyIntegration = {
      name: fieldValues.integrationName,
      protocol: fieldValues.integrationProtocol,
      method: fieldValues.httpMethod,
      endpoint: fieldValues.endpointUrl,
      auth:
        fieldValues.integrationProtocol === IntegrationProtocol.EMAIL
          ? {
              authType: "",
              authValue: "",
              encoding: "",
            }
          : {
              authType: fieldValues.authType,
              authValue: fieldValues.authValue,
              encoding: EncodingType.BASE64,
            },
      events: fieldValues.events ?? [],
    };

    const bodyTemplate = {
      content:
        fieldValues.templateContentType === ContentType.JSON
          ? formatJsonForApi(fieldValues.templateContent)
          : formatXmlForApi(fieldValues.templateContent),
      encoding: EncodingType.UTF8,
      contentType: fieldValues.templateContentType || ContentType.APPXML,
    };

    setUpdateIntegrationState({ loading: true, error: false });
    const [integrationResult, templateResult] = await Promise.all([
      updateIntegration(bodyIntegration),
      createIntegrationTemplate(bodyTemplate),
    ]);
    setUpdateIntegrationState({
      loading: false,
      error: integrationResult.error || templateResult.error,
    });

    //Need to update active cell so that context hooks fetch correct data
    if (!integrationResult.error) {
      setActiveCell((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          data: integrationResult.response.data,
        };
      });
      refreshIntegration();
      refreshIntegrationTemplate();
      refreshIntegrations();
    }
  };

  const onUpdateEmailIntegration = async () => {
    const fieldValues = await form.getFieldsValue();

    const bodyIntegration = {
      name: fieldValues.integrationName,
      protocol: fieldValues.integrationProtocol,
      method: fieldValues.httpMethod,
      endpoint: fieldValues.endpointUrl,
      auth:
        fieldValues.integrationProtocol === IntegrationProtocol.EMAIL
          ? {
              authType: "",
              authValue: "",
              encoding: "",
            }
          : {
              authType: fieldValues.authType,
              authValue: fieldValues.authValue,
              encoding: EncodingType.BASE64,
            },
      events: fieldValues.events ?? [],
    };

    setUpdateIntegrationState({ loading: true, error: false });
    const integrationResult = await updateIntegration(bodyIntegration);

    setUpdateIntegrationState({
      loading: false,
      error: integrationResult.error,
    });

    //Need to update active cell so that context hooks fetch correct data
    if (!integrationResult.error) {
      setActiveCell((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          data: integrationResult.response.data,
        };
      });
      refreshIntegrations();
      refreshIntegration();
    }
  };

  const onClick = async () => {
    if (
      form.getFieldValue("integrationProtocol") === IntegrationProtocol.HTTP
    ) {
      await onUpdateHttpIntegration();
    } else if (
      form.getFieldValue("integrationProtocol") === IntegrationProtocol.EMAIL
    ) {
      await onUpdateEmailIntegration();
    }
  };

  const disabled = useMemo(() => {
    return !activeCell || updateIntegrationState.disabled;
  }, [activeCell, updateIntegrationState.disabled]);

  return (
    <div data-testid="save-integration-button">
      <Button
        style={
          disabled
            ? { width: "200px", height: "40px" }
            : {
                backgroundColor: "#52c41a",
                borderColor: "#52c41a",
                color: "white",
                width: "200px",
                height: "40px",
              }
        }
        loading={updateIntegrationState.loading}
        disabled={disabled}
        onClick={onClick}
      >
        <SaveOutlined /> {t("save")}
      </Button>
    </div>
  );
}
